/**
 * CollaboratorsField (Workspace v2 shared assignment) — the drawer's "Collaborators"
 * / "Share with" control, in the PROCESS-OWNER area. Lists the card's collaborators
 * as chips (× to remove) and an "Add collaborator" picker. The picker reads the
 * workspace members and EXCLUDES the current owner + existing collaborators.
 *
 * GUARDRAIL: this is the process-owner side (assignee_user_id). It is deliberately
 * NOT rendered near "Assign care manager" — the care manager (assignee_roster_id)
 * is a single clinical owner, never shared (the two-assigns rule). Write controls
 * gate on canManageWorkItems (the server re-checks).
 *
 * Design pass 2026-07-25 (soak r2): the row joins THE drawer grid
 * (drawerLayout LABEL_COL) instead of carrying its own 122px label column
 * and its own dashed bottom border — the dividers now come from the row
 * list it sits in (DL6).
 */

import { useState, type ReactNode } from 'react';
import { useAddCollaborator, useRemoveCollaborator } from '@/lib/workspace';
import { AssigneeAvatar } from './Assignee';
import { TextAction } from './TextAction';
import { LABEL_COL } from './drawer/drawerLayout';
import type { CollaboratorWire, WorkspaceMemberWire } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';

export interface CollaboratorsFieldProps {
  tenantSlug: string;
  workItemId: string;
  /** The card's process owner (assignee_user_id) — excluded from the picker. */
  ownerUserId: string | null;
  collaborators: CollaboratorWire[];
  /** getWorkspaceMembers (manager-only); the picker source. */
  members: WorkspaceMemberWire[];
  canManage: boolean;
}

export function CollaboratorsField({
  tenantSlug,
  workItemId,
  ownerUserId,
  collaborators,
  members,
  canManage,
}: CollaboratorsFieldProps): ReactNode {
  const addCollaborator = useAddCollaborator(tenantSlug);
  const removeCollaborator = useRemoveCollaborator(tenantSlug);
  const [adding, setAdding] = useState(false);

  // Defensive: a stale client cache from before 0108 could carry a detail with no
  // collaborators field; treat it as empty rather than crashing the drawer.
  const list = collaborators ?? [];
  // Available to add = active members minus the owner minus existing collaborators.
  const existing = new Set(list.map((c) => c.user_id));
  const available = members.filter((m) => m.user_id !== ownerUserId && !existing.has(m.user_id));
  const busy = addCollaborator.isPending || removeCollaborator.isPending;

  return (
    <div className={`${LABEL_COL} py-1.5 text-[13px]`} data-testid="drawer-collaborators">
      <span className="text-sage-400">Collaborators</span>
      <div className="min-w-0 text-ink">
        {list.length === 0 && !canManage && <span className="text-sage-400">None</span>}
        {(list.length > 0 || canManage) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {list.map((c) => (
              <span
                key={c.user_id}
                data-testid="drawer-collaborator-chip"
                className="inline-flex items-center gap-1 bg-sage-50 text-sage-800 rounded-pill pl-1 pr-2 py-0.5"
              >
                <AssigneeAvatar name={c.name} size="sm" />
                <span className="text-[12px]">{c.name}</span>
                {canManage && (
                  <button
                    type="button"
                    data-testid="drawer-collaborator-remove"
                    aria-label={`Remove ${c.name}`}
                    disabled={busy}
                    onClick={() => removeCollaborator.mutate({ id: workItemId, userId: c.user_id })}
                    className="text-sage-400 hover:text-pepper disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded leading-none"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {canManage && list.length === 0 && !adding && (
              <span className="text-sage-400 text-[12px]">Share with…</span>
            )}
          </div>
        )}
        {canManage && (
          adding ? (
            <SelectField size="compact"
              autoFocus
              data-testid="drawer-collaborator-select"
              aria-label="Add collaborator"
              disabled={busy}
              defaultValue=""
              onChange={(e) => {
                const uid = e.target.value;
                if (uid) {
                  addCollaborator.mutate({ id: workItemId, userId: uid });
                  setAdding(false);
                }
              }}
              className="mt-1.5"
            >
              <option value="" disabled>Select a member</option>
              {available.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.name}</option>
              ))}
            </SelectField>
          ) : (
            available.length > 0 && (
              <TextAction
                data-testid="drawer-collaborator-add"
                disabled={busy}
                onClick={() => setAdding(true)}
                className="mt-1.5"
              >
                + Add collaborator
              </TextAction>
            )
          )
        )}
      </div>
    </div>
  );
}
